// ═══════════════════════════════════════════════════════════════
// PACE — Directory
// View all organization members' PACE profiles and compliance
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Box, Title, Text, Group, Stack, Paper, Table, Badge, TextInput,
  Select, SimpleGrid, Modal, Avatar, Center, Loader, Progress,
} from '@mantine/core';
import {
  IconSearch, IconUsers, IconCheck, IconAlertTriangle,
} from '@tabler/icons-react';
import { usePaceStore, PACE_TIERS, PACE_TIER_LABELS, PACE_TIER_COLORS, COMM_METHOD_LABELS } from '../../store/paceStore';
import type { PaceTier, PaceProfile } from '../../store/paceStore';

const TIER_BG: Record<PaceTier, { bg: string; border: string; dot: string }> = {
  primary:    { bg: 'var(--mantine-color-green-0)',  border: 'var(--mantine-color-green-3)',  dot: 'var(--mantine-color-green-5)' },
  alternate:  { bg: 'var(--mantine-color-blue-0)',   border: 'var(--mantine-color-blue-3)',   dot: 'var(--mantine-color-blue-5)' },
  contingent: { bg: 'var(--mantine-color-yellow-0)', border: 'var(--mantine-color-yellow-3)', dot: 'var(--mantine-color-yellow-5)' },
  emergency:  { bg: 'var(--mantine-color-red-0)',    border: 'var(--mantine-color-red-3)',    dot: 'var(--mantine-color-red-5)' },
};

interface PersonSummary {
  email: string;
  name: string;
  department: string;
  functionTitle: string;
  tiers: Map<PaceTier, PaceProfile>;
  filledCount: number;
  isCompliant: boolean;
  isStale: boolean;
  lastVerified: string;
}

export default function Directory() {
  const { profiles, loading, loadProfiles } = usePaceStore();
  const [search, setSearch] = useState('');
  const [filterCompliance, setFilterCompliance] = useState('all');
  const [selectedPerson, setSelectedPerson] = useState<PersonSummary | null>(null);

  useEffect(() => { loadProfiles(); }, []);

  // Group profiles by person
  const people = useMemo(() => {
    const map = new Map<string, PersonSummary>();
    profiles.forEach(p => {
      if (!p.personEmail) return;
      if (!map.has(p.personEmail)) {
        map.set(p.personEmail, {
          email: p.personEmail,
          name: p.personName,
          department: p.department,
          functionTitle: p.functionTitle,
          tiers: new Map(),
          filledCount: 0,
          isCompliant: false,
          isStale: false,
          lastVerified: '',
        });
      }
      const person = map.get(p.personEmail)!;
      if (p.contactValue && p.contactValue.trim()) {
        person.tiers.set(p.tier, p);
      }
      // Keep the most recent name/dept
      if (p.personName) person.name = p.personName;
      if (p.department) person.department = p.department;
      if (p.functionTitle) person.functionTitle = p.functionTitle;
      if (p.lastVerified && (!person.lastVerified || p.lastVerified > person.lastVerified)) {
        person.lastVerified = p.lastVerified;
      }
    });
    // Compute derived fields
    for (const person of map.values()) {
      person.filledCount = person.tiers.size;
      person.isCompliant = person.filledCount === 4;
      if (person.lastVerified) {
        person.isStale = Date.now() - new Date(person.lastVerified).getTime() > 180 * 86400000;
      } else {
        person.isStale = true;
      }
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [profiles]);

  // Filter
  const filtered = useMemo(() => {
    return people.filter(p => {
      if (filterCompliance === 'compliant' && !p.isCompliant) return false;
      if (filterCompliance === 'partial' && (p.isCompliant || p.filledCount === 0)) return false;
      if (filterCompliance === 'none' && p.filledCount > 0) return false;
      if (search) {
        const q = search.toLowerCase();
        return p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q)
          || p.department.toLowerCase().includes(q);
      }
      return true;
    });
  }, [people, search, filterCompliance]);

  // Stats
  const stats = useMemo(() => ({
    total: people.length,
    compliant: people.filter(p => p.isCompliant).length,
    partial: people.filter(p => p.filledCount > 0 && !p.isCompliant).length,
    stale: people.filter(p => p.isStale).length,
  }), [people]);

  const compliancePercent = stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0;

  if (loading && profiles.length === 0) {
    return <Center py="xl" mih={256}><Loader /></Center>;
  }

  return (
    <Box>
      {/* Header */}
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="black">PACE Directory</Title>
          <Text c="dimmed" size="sm" mt={4}>
            {stats.total} member{stats.total !== 1 ? 's' : ''} &middot; {stats.compliant} compliant &middot; {compliancePercent}% coverage
          </Text>
        </Box>
      </Group>

      {/* Stat cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        {[
          { label: 'Total Members', value: stats.total, color: 'gray' },
          { label: 'Compliant (4/4)', value: stats.compliant, color: 'green' },
          { label: 'Partial', value: stats.partial, color: 'yellow' },
          { label: 'Stale (>180d)', value: stats.stale, color: 'orange' },
        ].map(s => (
          <Paper key={s.label} p="sm" withBorder ta="center" style={{ borderTop: `3px solid var(--mantine-color-${s.color}-5)` }}>
            <Text size="xs" fw={500} tt="uppercase" c="dimmed">{s.label}</Text>
            <Text fz={28} fw={700} mt={2} c={s.color === 'gray' ? undefined : s.color}>{s.value}</Text>
          </Paper>
        ))}
      </SimpleGrid>

      {/* Compliance bar */}
      <Paper p="md" withBorder mb="md">
        <Group justify="space-between" mb={4}>
          <Text size="sm" fw={500}>Organization Compliance</Text>
          <Text size="sm" c="dimmed">{stats.compliant} / {stats.total} ({compliancePercent}%)</Text>
        </Group>
        <Progress value={compliancePercent} color="green" size="sm" radius="xl" />
      </Paper>

      {/* Filters */}
      <Group gap="sm" mb="md" wrap="wrap">
        <TextInput
          leftSection={<IconSearch size={14} />}
          placeholder="Search name, email, or department..."
          value={search}
          onChange={e => setSearch(e.currentTarget.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <Select
          data={[
            { value: 'all', label: 'All Compliance' },
            { value: 'compliant', label: 'Compliant (4/4)' },
            { value: 'partial', label: 'Partial' },
            { value: 'none', label: 'No Profile' },
          ]}
          value={filterCompliance}
          onChange={val => setFilterCompliance(val || 'all')}
          w={180}
        />
      </Group>

      {/* Table */}
      {filtered.length === 0 ? (
        <Center py="xl" mih={200}>
          <Stack align="center" gap="xs">
            <IconUsers size={40} color="#d1d5db" style={{ opacity: 0.3 }} />
            <Text fw={500} c="dimmed">
              {people.length === 0 ? 'No PACE profiles yet' : 'No members match filters'}
            </Text>
            <Text size="sm" c="dimmed">
              {people.length === 0
                ? 'Members will appear here once they fill in their PACE profile.'
                : 'Try adjusting your search or filter.'}
            </Text>
          </Stack>
        </Center>
      ) : (
        <Paper withBorder style={{ overflow: 'hidden' }}>
          <Table highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Member</Table.Th>
                <Table.Th visibleFrom="sm">Department</Table.Th>
                <Table.Th>PACE Tiers</Table.Th>
                <Table.Th visibleFrom="md">Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map(person => (
                <Table.Tr
                  key={person.email}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelectedPerson(person)}
                >
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <Avatar size={32} radius="xl" color="cyan">
                        {person.name?.[0]?.toUpperCase() || '?'}
                      </Avatar>
                      <Box>
                        <Text size="sm" fw={500}>{person.name || person.email}</Text>
                        <Text size="xs" c="dimmed">{person.email}</Text>
                      </Box>
                    </Group>
                  </Table.Td>
                  <Table.Td visibleFrom="sm">
                    <Text size="sm" c="dimmed">{person.department || '\u2014'}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} align="center">
                      <Group gap={2}>
                        {PACE_TIERS.map(t => (
                          <Box
                            key={t}
                            title={PACE_TIER_LABELS[t]}
                            w={6}
                            h={16}
                            style={{
                              borderRadius: 2,
                              background: person.tiers.has(t)
                                ? TIER_BG[t].dot
                                : 'var(--mantine-color-gray-2)',
                            }}
                          />
                        ))}
                      </Group>
                      <Text size="xs" c="dimmed">{person.filledCount}/4</Text>
                    </Group>
                  </Table.Td>
                  <Table.Td visibleFrom="md">
                    {person.isCompliant ? (
                      <Badge color="green" variant="light" size="sm" leftSection={<IconCheck size={10} />}>
                        Compliant
                      </Badge>
                    ) : person.filledCount > 0 ? (
                      <Badge color="yellow" variant="light" size="sm">
                        Partial
                      </Badge>
                    ) : (
                      <Badge color="gray" variant="light" size="sm">
                        No Profile
                      </Badge>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Box px="md" py="xs" style={{ borderTop: '1px solid var(--mantine-color-gray-2)', background: 'var(--mantine-color-gray-0)' }}>
            <Text size="xs" c="dimmed">Showing {filtered.length} of {people.length} members</Text>
          </Box>
        </Paper>
      )}

      {/* Member Detail Modal */}
      <Modal
        opened={!!selectedPerson}
        onClose={() => setSelectedPerson(null)}
        title={
          selectedPerson ? (
            <Group gap="sm">
              <Avatar size={36} radius="xl" color="cyan">
                {selectedPerson.name?.[0]?.toUpperCase() || '?'}
              </Avatar>
              <Box>
                <Text fw={600}>{selectedPerson.name}</Text>
                <Text size="sm" c="dimmed">{selectedPerson.email}</Text>
              </Box>
            </Group>
          ) : null
        }
        size="lg"
      >
        {selectedPerson && (
          <Stack gap="lg">
            {/* Profile info */}
            <Paper p="md" withBorder style={{ background: 'var(--mantine-color-gray-0)' }}>
              <SimpleGrid cols={3}>
                <Box>
                  <Text size="xs" c="dimmed">Department</Text>
                  <Text fw={500} size="sm" mt={2}>{selectedPerson.department || '\u2014'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Function / Title</Text>
                  <Text fw={500} size="sm" mt={2}>{selectedPerson.functionTitle || '\u2014'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed">Compliance</Text>
                  {selectedPerson.isCompliant ? (
                    <Badge color="green" variant="light" size="sm" mt={2}>Compliant</Badge>
                  ) : (
                    <Badge color="yellow" variant="light" size="sm" mt={2}>
                      {selectedPerson.filledCount}/4 Tiers
                    </Badge>
                  )}
                </Box>
              </SimpleGrid>
            </Paper>

            {/* Last verified */}
            {selectedPerson.lastVerified ? (
              <Group gap="xs">
                {selectedPerson.isStale ? (
                  <>
                    <IconAlertTriangle size={14} color="var(--mantine-color-yellow-6)" />
                    <Text size="xs" c="yellow.6">
                      Last verified {new Date(selectedPerson.lastVerified).toLocaleDateString()} (stale)
                    </Text>
                  </>
                ) : (
                  <>
                    <IconCheck size={14} color="var(--mantine-color-green-6)" />
                    <Text size="xs" c="green.6">
                      Verified {new Date(selectedPerson.lastVerified).toLocaleDateString()}
                    </Text>
                  </>
                )}
              </Group>
            ) : (
              <Group gap="xs">
                <IconAlertTriangle size={14} color="var(--mantine-color-yellow-6)" />
                <Text size="xs" c="yellow.6">Never verified</Text>
              </Group>
            )}

            {/* PACE Tiers */}
            <Box>
              <Text size="xs" fw={700} c="dimmed" tt="uppercase" mb="sm">PACE Profile</Text>
              <Stack gap="xs">
                {PACE_TIERS.map(t => {
                  const entry = selectedPerson.tiers.get(t);
                  const style = TIER_BG[t];
                  return (
                    <Paper
                      key={t}
                      p="sm"
                      withBorder
                      style={{
                        background: entry ? style.bg : 'var(--mantine-color-gray-0)',
                        borderColor: entry ? style.border : 'var(--mantine-color-gray-3)',
                      }}
                    >
                      <Group gap={8} mb={entry ? 8 : 0}>
                        <Box
                          w={8} h={8}
                          style={{
                            borderRadius: '50%',
                            background: entry ? style.dot : 'var(--mantine-color-gray-4)',
                            flexShrink: 0,
                          }}
                        />
                        <Badge color={entry ? PACE_TIER_COLORS[t] : 'gray'} variant="light" size="sm">
                          {PACE_TIER_LABELS[t]}
                        </Badge>
                        {entry && (
                          <Text size="xs" c="dimmed" ml="auto">
                            {COMM_METHOD_LABELS[entry.method]}
                          </Text>
                        )}
                      </Group>
                      {entry ? (
                        <Box pl={16}>
                          <Group gap="xs">
                            <Text size="xs" c="dimmed" w={52}>Contact</Text>
                            <Text size="sm" fw={500}>{entry.contactValue}</Text>
                          </Group>
                          {entry.backupValue && (
                            <Group gap="xs">
                              <Text size="xs" c="dimmed" w={52}>Backup</Text>
                              <Text size="sm">{entry.backupValue}</Text>
                            </Group>
                          )}
                          {entry.notes && (
                            <Group gap="xs">
                              <Text size="xs" c="dimmed" w={52}>Notes</Text>
                              <Text size="sm" fs="italic" c="dimmed">{entry.notes}</Text>
                            </Group>
                          )}
                        </Box>
                      ) : (
                        <Text size="xs" c="dimmed" fs="italic" pl={16}>Not filled in</Text>
                      )}
                    </Paper>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        )}
      </Modal>
    </Box>
  );
}
